import { useEffect, useState } from 'react';

import {
  listAdminUsers,
  type AdminUserRoleFilter,
  type AdminUserRow,
  type AdminUserStatusFilter,
} from '@/lib/db/admin/users';

export type AdminUsersFilter = {
  status: AdminUserStatusFilter;
  role: AdminUserRoleFilter;
  searchQuery: string;
  /** 0-based page index (ADR-056 — server pagination, §4.10). */
  page: number;
  pageSize: number;
};

export type AdminUsersState = {
  status: 'loading' | 'success' | 'error';
  rows: AdminUserRow[];
  total: number;
  error: Error | null;
};

const INITIAL: AdminUsersState = {
  status: 'loading',
  rows: [],
  total: 0,
  error: null,
};

export function useAdminUsers(filter: AdminUsersFilter, version = 0): AdminUsersState {
  const [state, setState] = useState<AdminUsersState>(INITIAL);

  const { status, role, searchQuery, page, pageSize } = filter;

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    listAdminUsers({ status, role, searchQuery, pageSize, offset: page * pageSize })
      .then((result) => {
        if (cancelled) return;
        setState({
          status: 'success',
          rows: result.rows,
          total: result.total,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          rows: [],
          total: 0,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [status, role, searchQuery, page, pageSize, version]);

  return state;
}
